import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Overlay } from 'components/structure/overlay/overlay';
import { Button } from 'components/elements/button';
import { Icon } from 'components/elements/icon';
import { Checkbox } from 'components/form/checkbox/checkbox';
import { openFlashMessage } from 'components/utils/flashMessages';
import { Colors } from 'components/utils/styles/ui';
import { downloadFile } from 'utils/fileFunctions';

import { FlashDefinition } from './utils/flashDefinition';
import i18n from './utils/i18n';
import * as styled from './styles/backupCodes';

type BackupCodesProps = {
  codes: string[];
  onClose: VoidCallback;
  isOpen: boolean;
  showWarning?: boolean;
  variant?: 'setup' | 'regenerate';
  noCloseOnBackgroundClick?: boolean;
  noClose?: boolean;
};

/* eslint-disable jsx-a11y/label-has-associated-control */
export const BackupCodes = ({
  codes,
  onClose,
  showWarning = false,
  isOpen,
  variant = 'setup',
  noCloseOnBackgroundClick = true,
  noClose = true,
}: BackupCodesProps) => {
  const intl = useIntl();
  const [confirmed, setConfirmed] = useState(false);
  const [copying, setCopying] = useState(false);

  const handleCopyCodes = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      openFlashMessage(FlashDefinition.backupCodesCopied);
    } catch (error) {
      openFlashMessage(FlashDefinition.backupCodesCopyFailed);
    } finally {
      setCopying(false);
    }
  };

  const handleDownloadCodes = () => {
    const fileTitle = intl.formatMessage(i18n.backupCodesFileTitle);
    const fileWarning = intl.formatMessage(i18n.backupCodesFileWarning);
    const codesText = `${fileTitle}
Generated: ${new Date().toLocaleString()}

${fileWarning}

${codes.join('\n')}
`;

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    downloadFile(url, 'candidately-backup-codes.txt');
    URL.revokeObjectURL(url);

    openFlashMessage(FlashDefinition.backupCodesDownloaded);
  };

  const handleOverlayClose = () => {
    if (
      (variant === 'regenerate' && (!showWarning || confirmed)) ||
      (variant === 'setup' && !noClose)
    ) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const overlayProps =
    variant === 'setup'
      ? { closeOverlay: onClose, noCloseOnBackgroundClick, noClose }
      : {
          closeOverlay: handleOverlayClose,
          noClose: true,
          noCloseOnBackgroundClick: true,
        };

  return (
    <Overlay {...overlayProps}>
      <styled.BackupCodesWrapper>
        <styled.BackupCodesHeader>
          <FormattedMessage {...i18n.backupCodesTitle} />
        </styled.BackupCodesHeader>

        <styled.Description>
          <FormattedMessage {...i18n.backupCodesDescription} />
        </styled.Description>

        {showWarning && (
          <styled.WarningBox>
            <Icon icon="Warning" color={Colors.yellowDark} />
            <span>
              <FormattedMessage {...i18n.flashBackupCodeWarning} />
            </span>
          </styled.WarningBox>
        )}

        <styled.CodesContainer>
          <styled.CodesGrid>
            {codes.map((code) => (
              <styled.Code key={`backup-code-${code}`}>{code}</styled.Code>
            ))}
          </styled.CodesGrid>
        </styled.CodesContainer>

        <styled.CheckboxWrapper>
          <Checkbox
            id="confirm-backup-codes"
            checked={confirmed}
            onChange={({ checked }: { id: string; checked: boolean }) =>
              setConfirmed(checked)
            }
          />
          <label htmlFor="confirm-backup-codes">
            <FormattedMessage {...i18n.confirmSavedCodes} />
          </label>
        </styled.CheckboxWrapper>

        <styled.ButtonGroup>
          <Button
            size="small"
            color="tealDark"
            onClick={handleCopyCodes}
            disabled={copying}
            loading={copying}
          >
            <Icon icon="Copy" size="small" />
            <FormattedMessage {...i18n.copyCodesButton} />
          </Button>
          <Button size="small" color="tealDark" onClick={handleDownloadCodes}>
            <Icon icon="Download" size="small" />
            <FormattedMessage {...i18n.downloadCodesButton} />
          </Button>
        </styled.ButtonGroup>

        <styled.Footer>
          <styled.Submit
            value={i18n.continueButton}
            onClick={onClose}
            disabled={!confirmed}
          />
        </styled.Footer>
      </styled.BackupCodesWrapper>
    </Overlay>
  );
};
